import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GetlifeContext } from "../context/GetLifeContext.context";
import {
  clickIdsFromQuery,
  readSealedClickIds,
  resolveClickIds,
  sealClickIds,
  withClickIds,
} from "../utils/clickIdUrl";

/**
 * Re-appends `gclid` / `gbraid` / `wbraid` to the URL after every route change
 * so `page_location` of every tag (GA4 -> sGTM -> Google Ads server-side
 * conversions) carries the click id, with or without advertising cookies —
 * the same situation as the ES marketing landing page. See utils/clickIdUrl.ts.
 *
 * Uses `replace` so the history stack is untouched, and only navigates when a
 * known id is actually missing from the URL (withClickIds returns null
 * otherwise), which rules out render loops.
 */
const ClickIdUrlKeeper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { gclid, gbraid, wbraid } = useContext(GetlifeContext);
  const sealed = useRef(false);

  useEffect(() => {
    const fromUrl = clickIdsFromQuery(new URLSearchParams(location.search));
    if (!sealed.current) {
      // Arrival: remember what the handoff URL carried, independently of cookies.
      sealClickIds(fromUrl);
      sealed.current = true;
    }
    const ids = resolveClickIds(fromUrl, { gclid, gbraid, wbraid }, readSealedClickIds());
    const search = withClickIds(location.search, ids);
    if (search === null) return;
    navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true, state: location.state });
  }, [location.pathname, location.search, location.hash, gclid, gbraid, wbraid]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default ClickIdUrlKeeper;
